// === src/pages/AITesting.jsx ===
import React, { useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { PlayCircle,CheckCircle,Download,ArrowRight,Sparkles } from "lucide-react";
import AIPopupModal from "../components/AIPopupModal";
import { triggerAIPopup } from "../utils/triggerAIPopup";
import learningLinks from "../data/learningLinks";
import { useIdea } from "../context/IdeaContext";
import { useAuth } from "../context/AuthContext";
import ModuleHeader from "../components/ModuleHeader";
import buildMDIHPdfName from "../utils/pdfNaming";

const AITesting = () => {

const navigate = useNavigate();
const location = useLocation();

const {
markModuleComplete,
aiTestingFeedback,
userId,
setUserId,
selectedIdea,
} = useIdea();

 /* RESOLVE STARTUP NAME (CONSISTENT WITH PLATFORM) */

    const startupName =

    selectedIdea?.ideaTitle ||

    selectedIdea?.title ||

    selectedIdea?.idea ||

    selectedIdea?.problemStatement ||

    businessModel?.startupName ||

    businessModel?.idea ||

    "Startup";
    
const { user } = useAuth();

/* USER SYNC */

useEffect(()=>{

if(!userId && user?._id)
setUserId(user._id);

else if(!user && !userId)
setUserId("guest");

},[user,userId,setUserId]);

const activeUserId =
userId || user?._id || "guest";

const ideaId =
selectedIdea?.ideaId ||
location.state?.ideaId ||
null;

const aiTestingRecordId =
location.state?.aiTestingRecordId ||
location.state?.recordId ||
null;

const mvpPlanRecordId =
location.state?.mvpPlanRecordId ||
location.state?.mvpPlannerRecordId ||
null;

const mvpAgentRecordId =
location.state?.mvpAgentRecordId ||
location.state?.mvpAgentRecordId ||
null;

/* STATES */

const [aiResults,setAiResults]=
useState(aiTestingFeedback || null);

const [popupMessage,setPopupMessage]=
useState("");

const [loading,setLoading]=
useState(false);

const [loadedFromDB,setLoadedFromDB]=
useState(false);

const [error,setError]=
useState("");

const [mvpPlan,setMvpPlan]=
useState([]);

const [mvpAgent,setMvpAgent]=
useState(null);

const [businessModel,setBusinessModel]=
useState(null);

const [idea,setIdea]=
useState("");

/* PREFILL */

useEffect(()=>{

if(location.state?.mvpPlan){

let plan = location.state.mvpPlan;

setMvpPlan(

typeof plan==="string"
? [{section:"MVP Plan",details:plan}]
: plan

);

}

if(location.state?.mvpAgent){

let agent = location.state.mvpAgent;

setMvpAgent(

typeof agent==="string"
? [{section:"MVP Agent Output",details:agent}]
: agent

);

}

if(location.state?.businessModel)
setBusinessModel(location.state.businessModel);

if(location.state?.idea)
setIdea(location.state.idea);

const storedMvp =
localStorage.getItem("mvpPlanner");

if((!mvpPlan || !mvpPlan.length) && storedMvp){

try{

setMvpPlan(JSON.parse(storedMvp));

}catch{}

}

const storedAgent=
localStorage.getItem("mvpAgentOutput");

if(!mvpAgent && storedAgent){

try{

setMvpAgent(JSON.parse(storedAgent));

}catch{}

}

},[location.state,ideaId]);

/* RUN TEST */

const runAITests = async ()=>{

const hasMvpPlan =
mvpPlan &&
(
(Array.isArray(mvpPlan) && mvpPlan.length) ||
Object.keys(mvpPlan).length
);

const hasMvpAgent =
mvpAgent &&
(
(Array.isArray(mvpAgent) && mvpAgent.length) ||
Object.keys(mvpAgent).length
);

if(!hasMvpPlan && !hasMvpAgent && !businessModel){

setError(
"❗ Please complete MVP Planner or MVP Agent before running AI Testing."
);

return;

}

setLoading(true);
setError("");

try{

const payload={

idea:
idea ||
selectedIdea?.title ||
selectedIdea?.ideaTitle ||
null,

ideaId,

recordId:aiTestingRecordId,

mvpPlan,

mvpAgent,

businessModel,

mvpPlanRecordId,

mvpAgentRecordId,

targetUserSegments:[],

userId:activeUserId

};

const {data} =
await axios.post(
"/api/ai-testing",
payload
);

if(!data?.success || !data.results){

setError(
"Failed to run AI testing. Please try again."
);

return;

}

const syntheticUsers =
Array.isArray(data.results.syntheticUsers)
? data.results.syntheticUsers
: [];

const enrichedResults = {

...data.results,

syntheticUsers

};

setAiResults(enrichedResults);

markModuleComplete(
"aiTesting",
enrichedResults
);

const popup = await triggerAIPopup({

userId:activeUserId,

ideaId,

recordId:aiTestingRecordId,

moduleName:"AI Testing",

stepName:"Testing Completed",

context:`AI Testing completed for ideaId=${ideaId}`

});

if(popup)
setPopupMessage(popup);

}catch(err){

setError(

err?.response?.data?.error ||
"Failed to run AI testing."

);

}
finally{

setLoading(false);

}

};

/* DOWNLOAD PDF ⭐ NEW */

const downloadPDF = async ()=>{

if(!aiResults) return;

try{

const token =
localStorage.getItem("token");

const res =
await axios.post(

"/api/pdf/ai-testing",

{ideaId},

{

headers:{
Authorization:`Bearer ${token}`
},

responseType:"blob"

}

);

const url =
window.URL.createObjectURL(

new Blob(
[res.data],
{type:"application/pdf"}
)

);

const link =
document.createElement("a");

link.href=url;

link.download =
buildMDIHPdfName(startupName,"Validation");

document.body.appendChild(link);

link.click();

link.remove();

}catch(err){

console.error(
"❌ AI Testing PDF failed:",
err
);

}

};

/* NAV */

const goToMVPPlanner=()=>
navigate(
"/mvp-planner",
{state:{ideaId,idea}}
);

const goToMVPAgent=()=>
navigate(
"/mvp-agent",
{state:{ideaId,idea}}
);

const handleGoToBusinessModel=()=>{

navigate(

"/business-model-builder",

{

state:{

aiRefinedModel:
aiResults?.refinedModel ||
businessModel,

idea,

mvpPlan,

mvpAgent,

ideaId,

userId:activeUserId

}

}

);

};

/* CHART */

const chartData =
aiResults?.aiScores
?Object.entries(
aiResults.aiScores
).map(
([metric,score])=>({

metric,
score

})
)
:[];

/* UI */

return(

<div className="bg-slate-50 min-h-screen">

<div className="max-w-6xl mx-auto px-6 py-10 space-y-8">

<ModuleHeader

title="AI Validation"

subtitle="Simulate real user reactions, feasibility risks and execution gaps before scaling."

currentModule="aiTesting"

isLoadedFromDB={loadedFromDB}

/>

  {/* ACTIVE IDEA */}

  <div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6">

  <b>Startup:</b> {startupName}

  </div>

{error && (

<div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 rounded">

{error}

</div>

)}

<div className="flex flex-wrap items-center gap-3">

<button

onClick={runAITests}

disabled={loading}

className={`px-7 py-2.5 rounded-lg text-sm font-semibold text-white transition ${
loading
? "bg-gray-400"
: "bg-blue-600 hover:bg-blue-700"
}`}

>

{loading
? "Running AI Validation..."
: "Run AI Validation Tests"}

</button>

<button
onClick={goToMVPPlanner}
className="text-blue-600 hover:underline"
>

← Back to MVP Planner

</button>

<button
onClick={goToMVPAgent}
className="text-blue-600 hover:underline"
>

← Back to MVP Agent

</button>

</div>

{/* RESULTS */}

{aiResults && (

<div className="space-y-6">

<div className="p-6 bg-white rounded-xl shadow space-y-6">

{/* HEADER WITH DOWNLOAD ⭐ */}

<div className="flex justify-between items-center">

<h3 className="text-xl font-semibold text-blue-900">

AI Test Results

</h3>

<button

onClick={downloadPDF}

disabled={!aiResults}

className={`mt-4 bg-green-600 hover:bg-blue-700
text-white px-6 py-3 rounded-lg font-semibold
inline-flex items-center gap-2 transition
${!aiResults ? "opacity-50 cursor-not-allowed" : ""}`}

>
<Download size={18} />

📄 Download AI Report

</button>

</div>

{/* rest unchanged */}

{chartData.length>0 && (

<div className="h-72">

<ResponsiveContainer width="100%" height="100%">

<RadarChart data={chartData}>

<PolarGrid/>

<PolarAngleAxis dataKey="metric"/>

<PolarRadiusAxis domain={[0,10]}/>

<Radar
dataKey="score"
stroke="#2563eb"
fill="#2563eb"
fillOpacity={0.6}
/>

<Tooltip/>

</RadarChart>

</ResponsiveContainer>

</div>

)}

<div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">

<p className="text-sm text-blue-800 italic">

{aiResults.feedbackSummary}

</p>

</div>

<div>

<h4 className="font-semibold">
Suggested Improvements
</h4>

<p className="text-sm text-slate-600 mt-1">

{aiResults.suggestedImprovements}

</p>

</div>

{aiResults.syntheticUsers?.length>0 && (

<div>

<h4 className="font-semibold mb-3">

👥 Synthetic User Feedback

</h4>

<div className="grid md:grid-cols-2 gap-4">

{aiResults.syntheticUsers.map((u,idx)=>(

<div
key={idx}
className="border rounded-lg p-4 bg-slate-50"
>

<h5 className="font-semibold text-blue-700">

{u.persona}

</h5>

<p className="text-xs mt-1">

⭐ Interest Score:
{u.interestScore}/10

</p>

<p className="text-sm text-slate-600 mt-2">

{u.feedback}

</p>

{u.concerns?.length>0 &&(

<ul className="mt-2 list-disc list-inside text-xs text-slate-500">

{u.concerns.map((c,i)=>(
<li key={i}>{c}</li>
))}

</ul>

)}

</div>

))}

</div>

</div>

)}

</div>

{/* CTA unchanged */}

<div className="pt-4 space-y-2 border-t border-slate-200">

<h3 className="text-lg font-semibold text-blue-900">

Ready to Strengthen Your Business Model?

</h3>

<p className="text-sm text-slate-600 max-w-xl">

Apply AI-identified risks and opportunities.

</p>

<button

onClick={handleGoToBusinessModel}

className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"

>

Go To Business Model Builder →

</button>

</div>

</div>

)}

{/* LEARNING */}

{learningLinks["ai-testing"] && (

<div className="pt-6 border-t border-slate-200">

<button

onClick={()=>navigate(

`/learning-mentorship?video=${learningLinks["ai-testing"].videoId}`

)}

className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm hover:bg-blue-700 transition"

>

🎓 {learningLinks["ai-testing"].title}

</button>

</div>

)}

<AIPopupModal

message={popupMessage}

onClose={()=>setPopupMessage("")}

/>

</div>

</div>

);

};

export default AITesting;